<template>
  <div class="area-restituicao p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-undo-alt"></i>
      Restituição de Taxa de Inscrição
    </h5>

    <!-- Inscrições Disponíveis para Restituição -->
    <div class="section-header mb-3">
      <i class="fas fa-clipboard-list"></i>
      <span>Inscrições Disponíveis para Restituição</span>
    </div>

    <!-- Loading Inscrições -->
    <div v-if="loadingInscricoes" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando...</span>
      </div>
      <p class="text-muted mt-3">Carregando inscrições...</p>
    </div>

    <!-- Empty State - Sem Inscrições -->
    <div v-else-if="!inscricoesAtivas.length" class="empty-state">
      <i class="fas fa-clipboard-list"></i>
      <p class="empty-title">Nenhuma inscrição ativa encontrada</p>
      <p class="empty-subtitle">Você não possui inscrições ativas no momento.</p>
      <small class="empty-hint">Somente inscrições ativas podem solicitar restituição.</small>
    </div>

    <!-- Tabela de Inscrições -->
    <div v-else class="table-responsive mb-4">
      <table class="table table-hover">
        <thead class="table-dark">
          <tr>
            <th><i class="fas fa-building"></i> Concurso</th>
            <th><i class="fas fa-briefcase"></i> Vaga</th>
            <th><i class="fas fa-dollar-sign"></i> Valor</th>
            <th class="text-center"><i class="fas fa-cogs"></i> Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="inscricao in inscricoesAtivas" :key="inscricao.id">
            <td>
              <strong>{{ (inscricao.concurso && inscricao.concurso.prefeitura) || 'N/A' }}</strong>
              <br>
              <small class="text-muted">ID: #{{ inscricao.id }}</small>
            </td>
            <td>{{ (inscricao.vaga && inscricao.vaga.cargo) || 'N/A' }}</td>
            <td>
              <span class="badge badge-info">
                R$ {{ formatarValor((inscricao.concurso && inscricao.concurso.valor_inscricao) || 0) }}
              </span>
            </td>
            <td class="text-center">
              <div v-if="!inscricao.reembolso_solicitado">
                <button
                  class="btn btn-sm btn-primary"
                  @click="abrirModal(inscricao)"
                >
                  <i class="fas fa-hand-holding-usd"></i>
                  Solicitar Restituição
                </button>
              </div>
              <div v-else class="d-flex flex-column align-items-center gap-1">
                <span :class="getClasseStatus(getReembolsoDaInscricao(inscricao.id)?.status)">
                  <i class="fas fa-check-circle"></i>
                  {{ getReembolsoDaInscricao(inscricao.id)?.status || 'Solicitado' }}
                </span>
                <button
                  v-if="getReembolsoDaInscricao(inscricao.id)?.status === 'Solicitado'"
                  class="btn btn-xs btn-outline-danger mt-1"
                  @click="cancelar(getReembolsoDaInscricao(inscricao.id))"
                  title="Cancelar solicitação"
                >
                  <i class="fas fa-times"></i>
                  Cancelar
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Minhas Solicitações de Restituição -->
    <div class="section-header mb-3">
      <i class="fas fa-list-alt"></i>
      <span>Minhas Solicitações de Restituição</span>
    </div>

    <!-- Loading Reembolsos -->
    <div v-if="loadingReembolsos" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando...</span>
      </div>
      <p class="text-muted mt-3">Carregando solicitações...</p>
    </div>

    <!-- Empty State - Sem Reembolsos -->
    <div v-else-if="!reembolsos.length" class="empty-state">
      <i class="fas fa-inbox"></i>
      <p class="empty-title">Nenhuma solicitação de restituição</p>
      <p class="empty-subtitle">Você ainda não solicitou nenhuma restituição.</p>
    </div>

    <!-- Tabela de Reembolsos -->
    <div v-else class="table-responsive">
      <table class="table table-hover">
        <thead class="table-dark">
          <tr>
            <th><i class="fas fa-hashtag"></i> ID</th>
            <th><i class="fas fa-building"></i> Concurso</th>
            <th><i class="fas fa-briefcase"></i> Vaga</th>
            <th><i class="fas fa-calendar"></i> Data Solicitação</th>
            <th><i class="fas fa-dollar-sign"></i> Valor</th>
            <th><i class="fas fa-info-circle"></i> Status</th>
            <th class="text-center"><i class="fas fa-cogs"></i> Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="reembolso in reembolsos" :key="reembolso.id">
            <td>#{{ reembolso.id }}</td>
            <td>
              <strong>{{ (reembolso.inscricao && reembolso.inscricao.concurso && reembolso.inscricao.concurso.prefeitura) || 'N/A' }}</strong>
              <br>
              <small class="text-muted">Inscrição #{{ reembolso.inscricao_id }}</small>
            </td>
            <td>{{ (reembolso.inscricao && reembolso.inscricao.vaga && reembolso.inscricao.vaga.cargo) || 'N/A' }}</td>
            <td>{{ formatarData(reembolso.data_solicitacao) }}</td>
            <td>
              <span class="badge badge-info">
                R$ {{ formatarValor(reembolso.valor_reembolso) }}
              </span>
            </td>
            <td>
              <span :class="getClasseStatus(reembolso.status)">
                {{ reembolso.status }}
              </span>
            </td>
            <td class="text-center">
              <button
                class="btn btn-sm btn-info mr-2"
                @click="abrirDetalhes(reembolso)"
              >
                <i class="fas fa-eye"></i>
                Ver
              </button>
              <button
                v-if="reembolso.status === 'Solicitado'"
                class="btn btn-sm btn-danger"
                @click="cancelar(reembolso)"
              >
                <i class="fas fa-times"></i>
                Cancelar
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal de Solicitação -->
    <div v-if="showModal" class="modal-overlay" @click="fecharModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-hand-holding-usd"></i>
            Solicitar Restituição
          </h4>
          <button @click="fecharModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Loading Overlay no Modal -->
        <div v-if="loadingModal" class="modal-loading-overlay">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Carregando...</span>
          </div>
          <p class="mt-3">Carregando informações...</p>
        </div>

        <form @submit.prevent="enviarSolicitacao">
          <div class="modal-body">
            <!-- Informações da Inscrição -->
            <div v-if="inscricaoSelecionada" class="info-box mb-4">
              <h6 class="info-title">
                <i class="fas fa-info-circle"></i>
                Informações da Inscrição
              </h6>
              <div class="info-row">
                <strong><i class="fas fa-building"></i> Concurso:</strong>
                <span>{{ inscricaoSelecionada.concurso && inscricaoSelecionada.concurso.prefeitura }}</span>
              </div>
              <div class="info-row">
                <strong><i class="fas fa-briefcase"></i> Vaga:</strong>
                <span>{{ inscricaoSelecionada.vaga && inscricaoSelecionada.vaga.cargo }}</span>
              </div>
              <div class="info-row">
                <strong><i class="fas fa-dollar-sign"></i> Valor:</strong>
                <span>R$ {{ formatarValor((inscricaoSelecionada.concurso && inscricaoSelecionada.concurso.valor_inscricao) || 0) }}</span>
              </div>
            </div>

            <!-- Texto do Requerimento -->
            <div class="requerimento-box mb-4">
              <p class="text-requerimento">
                Eu <strong>{{ profile.nome }}</strong>, portador(a) do CPF n.º <strong>{{ formatarCPF(profile.cpf) }}</strong>,
                inscrito(a) no Processo Seletivo, para o cargo de <strong>{{ (inscricaoSelecionada && inscricaoSelecionada.vaga && inscricaoSelecionada.vaga.cargo) || 'N/A' }}</strong>,
                inscrição n.º <strong>#{{ (inscricaoSelecionada && inscricaoSelecionada.id) || '-' }}</strong>, solicito a restituição do valor pago pela taxa de inscrição.
              </p>
            </div>

            <!-- Tipo de Restituição -->
            <div class="form-group mb-4">
              <label class="form-label">
                <i class="fas fa-wallet"></i>
                Escolha a forma de restituição: <span class="text-danger">*</span>
              </label>
              <div class="radio-group">
                <label class="radio-option">
                  <input type="radio" v-model="form.tipo_restituicao" :value="1" required>
                  <span>Opção 01 - Devolução na Conta Bancária do Candidato CPF n.º {{ formatarCPF(profile.cpf) }}</span>
                </label>
                <label class="radio-option">
                  <input type="radio" v-model="form.tipo_restituicao" :value="2" required>
                  <span>Opção 02 - Devolução na Conta Bancária de Terceiros</span>
                </label>
                <label class="radio-option">
                  <input type="radio" v-model="form.tipo_restituicao" :value="3" required>
                  <span>Opção 03 - Chave PIX</span>
                </label>
              </div>
            </div>

            <!-- Dados Bancários (Opções 1 e 2) -->
            <div v-if="form.tipo_restituicao !== 3" class="dados-bancarios-container mb-4">
              <h6 class="mb-3">
                <i class="fas fa-university"></i>
                Dados Bancários
              </h6>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Banco <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="form.banco" placeholder="Ex: Banco do Brasil" required>
                </div>
                <div class="col-md-3 mb-3">
                  <label class="form-label">Agência <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="form.agencia" placeholder="Ex: 1234" required>
                </div>
                <div class="col-md-3 mb-3">
                  <label class="form-label">Conta <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="form.conta" placeholder="Ex: 12345-6" required>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Tipo de Conta <span class="text-danger">*</span></label>
                  <div class="radio-group-inline">
                    <label class="radio-option-inline">
                      <input type="radio" v-model="form.tipo_conta" value="cc" required>
                      <span>Conta Corrente</span>
                    </label>
                    <label class="radio-option-inline">
                      <input type="radio" v-model="form.tipo_conta" value="cp" required>
                      <span>Conta Poupança</span>
                    </label>
                  </div>
                </div>

                <!-- CPF do Terceiro (apenas Opção 2) -->
                <div v-if="form.tipo_restituicao === 2" class="col-md-6 mb-3">
                  <label class="form-label">CPF do Titular <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.titular_documento"
                    v-mask="'###.###.###-##'"
                    placeholder="000.000.000-00"
                    required
                  >
                </div>
              </div>
            </div>

            <!-- Chave PIX (Opção 3) -->
            <div v-else class="chave-pix-container mb-4">
              <h6 class="mb-3">
                <i class="fas fa-qrcode"></i>
                Chave PIX
              </h6>

              <div class="row">
                <div class="col-md-12">
                  <label class="form-label">Chave PIX <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.chavePix"
                    placeholder="CPF, E-mail, Celular ou Chave Aleatória"
                    required
                  >
                  <small class="form-text text-muted">
                    <i class="fas fa-info-circle"></i>
                    Informe sua chave PIX (CPF, e-mail, celular ou chave aleatória)
                  </small>
                </div>
              </div>
            </div>

            <!-- Motivo (Opcional) -->
            <div class="form-group mb-4">
              <label class="form-label">
                <i class="fas fa-comment"></i>
                Motivo da Solicitação (Opcional)
              </label>
              <textarea
                class="form-control"
                v-model="form.motivo"
                rows="3"
                placeholder="Descreva o motivo da solicitação de restituição..."
                maxlength="1000"
              ></textarea>
              <small class="form-text text-muted">{{ (form.motivo && form.motivo.length) || 0 }}/1000 caracteres</small>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModal">
              <i class="fas fa-times"></i>
              Cancelar
            </button>
            <button type="submit" class="btn btn-primary" :disabled="enviando">
              <i :class="enviando ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
              {{ enviando ? 'Enviando...' : 'Enviar Solicitação' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal de Detalhes -->
    <div v-if="showDetalhes" class="modal-overlay" @click="fecharDetalhes">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-file-alt"></i>
            Detalhes da Solicitação
          </h4>
          <button @click="fecharDetalhes" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="reembolsoSelecionado" class="detalhes-container">
            <!-- Informações da Inscrição -->
            <div class="info-box mb-4">
              <h6 class="info-title">
                <i class="fas fa-clipboard-check"></i>
                Informações da Inscrição
              </h6>
              <div class="info-row">
                <strong>ID da Inscrição:</strong>
                <span>#{{ reembolsoSelecionado.inscricao_id }}</span>
              </div>
              <div class="info-row">
                <strong>Concurso:</strong>
                <span>{{ reembolsoSelecionado.inscricao && reembolsoSelecionado.inscricao.concurso && reembolsoSelecionado.inscricao.concurso.prefeitura }}</span>
              </div>
              <div class="info-row">
                <strong>Vaga:</strong>
                <span>{{ reembolsoSelecionado.inscricao && reembolsoSelecionado.inscricao.vaga && reembolsoSelecionado.inscricao.vaga.cargo }}</span>
              </div>
            </div>

            <!-- Informações do Reembolso -->
            <div class="info-box mb-4">
              <h6 class="info-title">
                <i class="fas fa-info-circle"></i>
                Informações do Reembolso
              </h6>
              <div class="info-row">
                <strong>ID do Reembolso:</strong>
                <span>#{{ reembolsoSelecionado.id }}</span>
              </div>
              <div class="info-row">
                <strong>Data Solicitação:</strong>
                <span>{{ formatarData(reembolsoSelecionado.data_solicitacao) }}</span>
              </div>
              <div v-if="reembolsoSelecionado.data_processamento" class="info-row">
                <strong>Data Processamento:</strong>
                <span>{{ formatarData(reembolsoSelecionado.data_processamento) }}</span>
              </div>
              <div class="info-row">
                <strong>Valor:</strong>
                <span>R$ {{ formatarValor(reembolsoSelecionado.valor_reembolso) }}</span>
              </div>
              <div class="info-row">
                <strong>Status:</strong>
                <span :class="getClasseStatus(reembolsoSelecionado.status)">
                  {{ reembolsoSelecionado.status }}
                </span>
              </div>
            </div>

            <!-- Dados Bancários / PIX -->
            <div class="info-box mb-4">
              <h6 class="info-title">
                <i class="fas fa-university"></i>
                Dados para Restituição
              </h6>
              <div v-if="reembolsoSelecionado.chavePix">
                <div class="info-row">
                  <strong>Chave PIX:</strong>
                  <span>{{ reembolsoSelecionado.chavePix }}</span>
                </div>
              </div>
              <div v-else>
                <div class="info-row">
                  <strong>Banco:</strong>
                  <span>{{ reembolsoSelecionado.banco }}</span>
                </div>
                <div class="info-row">
                  <strong>Agência:</strong>
                  <span>{{ reembolsoSelecionado.agencia }}</span>
                </div>
                <div class="info-row">
                  <strong>Conta:</strong>
                  <span>{{ reembolsoSelecionado.conta }} ({{ reembolsoSelecionado.tipo_conta === 'cc' ? 'Corrente' : 'Poupança' }})</span>
                </div>
                <div v-if="reembolsoSelecionado.titular_documento" class="info-row">
                  <strong>CPF Titular:</strong>
                  <span>{{ formatarCPF(reembolsoSelecionado.titular_documento) }}</span>
                </div>
              </div>
            </div>

            <!-- Motivo -->
            <div v-if="reembolsoSelecionado.motivo" class="info-box mb-4">
              <h6 class="info-title">
                <i class="fas fa-comment"></i>
                Motivo da Solicitação
              </h6>
              <p class="mb-0">{{ reembolsoSelecionado.motivo }}</p>
            </div>

            <!-- Observação do Admin -->
            <div v-if="reembolsoSelecionado.observacao_admin" class="alert alert-info">
              <i class="fas fa-user-shield"></i>
              <div>
                <strong>Observação do Administrador:</strong>
                <p class="mb-0 mt-2">{{ reembolsoSelecionado.observacao_admin }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="fecharDetalhes">
            <i class="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios"
import { mapGetters } from "vuex"

export default {
  name: 'Restituicao',

  data() {
    return {
      inscricoes: [],
      reembolsos: [],
      reembolsosPorInscricao: {}, // Mapear reembolsos por ID de inscrição
      inscricaoSelecionada: null,
      reembolsoSelecionado: null,
      loadingInscricoes: false,
      loadingReembolsos: false,
      loadingModal: false,
      enviando: false,
      showModal: false,
      showDetalhes: false,
      form: {
        tipo_restituicao: 1,
        banco: '',
        agencia: '',
        conta: '',
        tipo_conta: '',
        titular_documento: '',
        chavePix: '',
        motivo: ''
      }
    }
  },

  computed: {
    ...mapGetters({ profile: "profile/getProfile" }),
    
    inscricoesAtivas() {
      return this.inscricoes.filter(inscricao => inscricao.status === 'A')
    }
  },

  created() {
    // console.log('Restituição: Componente criado')
    this.carregarDados()
  },

  methods: {
    async carregarDados() {
      // Carregar inscrições e reembolsos em paralelo
      await Promise.all([
        this.carregarInscricoes(),
        this.carregarReembolsos()
      ])
      
      // Mapear reembolsos por inscrição
      this.mapearReembolsos()
    },

    async carregarInscricoes() {
      try {
        this.loadingInscricoes = true
        // console.log('Carregando inscrições do candidato...')
        
        const { data } = await axios.get('/ps/inscricao')
        this.inscricoes = data || []
        
        // console.log('✅ Inscrições carregadas:', this.inscricoes.length)
      } catch (error) {
        console.error('❌ Erro ao carregar inscrições:', error)
        this.inscricoes = []
        this.$sweet && this.$sweet.sweetMensagem && this.$sweet.sweetMensagem('Erro ao carregar inscrições.')
      } finally {
        this.loadingInscricoes = false
      }
    },

    async carregarReembolsos() {
      try {
        this.loadingReembolsos = true
        // console.log('Carregando reembolsos do candidato...')
        
        const { data } = await axios.get('/ps/reembolso')
        this.reembolsos = data || []
        
        // console.log('✅ Reembolsos carregados:', this.reembolsos.length)
      } catch (error) {
        console.error('❌ Erro ao carregar reembolsos:', error)
        this.reembolsos = []
        
        // Tratar melhor o erro
        if (error.response) {
          console.error('Resposta do servidor:', error.response.data)
          const mensagem = error.response.data?.error || error.response.data?.message || 'Erro ao carregar reembolsos.'
          this.$sweet && this.$sweet.showError && this.$sweet.showError(mensagem)
        } else {
          console.error('Erro de rede:', error.message)
          this.$sweet && this.$sweet.showError && this.$sweet.showError('Erro de conexão. Verifique sua internet.')
        }
      } finally {
        this.loadingReembolsos = false
      }
    },

    mapearReembolsos() {
      // console.log('Mapeando reembolsos por inscrição...')
      
      // Criar mapa de reembolsos por inscrição_id
      const mapa = {}
      this.reembolsos.forEach(reembolso => {
        if (reembolso.inscricao_id) {
          mapa[reembolso.inscricao_id] = reembolso
        }
      })
      
      this.reembolsosPorInscricao = mapa
      
      // Atualizar flag nas inscrições
      this.inscricoes.forEach((inscricao, index) => {
        const temReembolso = !!mapa[inscricao.id]
        this.$set(this.inscricoes[index], 'reembolso_solicitado', temReembolso)
        
        if (temReembolso) {
          this.$set(this.inscricoes[index], 'reembolso', mapa[inscricao.id])
        }
      })
      
      // console.log('✅ Mapeamento concluído:', {
      //   total_inscricoes: this.inscricoes.length,
      //   com_reembolso: Object.keys(mapa).length,
      //   mapa: mapa
      // })
    },

    abrirModal(inscricao) {
      this.inscricaoSelecionada = inscricao
      this.showModal = true
      this.resetForm()
    },

    fecharModal() {
      this.showModal = false
      this.inscricaoSelecionada = null
      this.resetForm()
    },

    resetForm() {
      this.form = {
        tipo_restituicao: 1,
        banco: '',
        agencia: '',
        conta: '',
        tipo_conta: '',
        titular_documento: '',
        chavePix: '',
        motivo: ''
      }
    },

    async enviarSolicitacao() {
      if (!this.inscricaoSelecionada) {
        this.$sweet && this.$sweet.sweetError && this.$sweet.sweetError('Selecione uma inscrição.')
        return
      }

      try {
        this.enviando = true
        this.$sweet && this.$sweet.sweetWait && this.$sweet.sweetWait('Enviando solicitação...')

        // // console.log('Enviando solicitação de reembolso:', {
        //   inscricao_id: this.inscricaoSelecionada.id,
        //   form: this.form
        // })

        await axios.post(
          `/ps/reembolso/${this.inscricaoSelecionada.id}/solicitar`,
          this.form
        )

        // console.log('✅ Solicitação enviada com sucesso:', data)

        this.$sweet && this.$sweet.sweetSuccess && this.$sweet.sweetSuccess('Solicitação de restituição enviada com sucesso!')

        // Fechar modal e recarregar dados
        this.fecharModal()
        await this.carregarDados()
      } catch (error) {
        console.error('❌ Erro ao enviar solicitação:', error)
        
        let errorMessage = 'Erro ao enviar solicitação.'
        if (error.response && error.response.data) {
          errorMessage = error.response.data.error || error.response.data.message || errorMessage
        }
        
        this.$sweet && this.$sweet.sweetError && this.$sweet.sweetError(errorMessage)
      } finally {
        this.enviando = false
      }
    },

    abrirDetalhes(reembolso) {
      this.reembolsoSelecionado = reembolso
      this.showDetalhes = true
    },

    fecharDetalhes() {
      this.showDetalhes = false
      this.reembolsoSelecionado = null
    },

    async cancelar(reembolso) {
      const confirmacao = this.$sweet && this.$sweet.sweetConfirm
        ? await this.$sweet.sweetConfirm('Tem certeza que deseja cancelar esta solicitação de restituição?')
        : confirm('Tem certeza que deseja cancelar esta solicitação de restituição?')

      if (!confirmacao) return

      try {
        this.$sweet && this.$sweet.sweetWait && this.$sweet.sweetWait('Cancelando solicitação...')
        
        // console.log('Cancelando reembolso:', reembolso.id)
        
        await axios.delete(`/ps/reembolso/${reembolso.id}`)
        
        // console.log('✅ Reembolso cancelado com sucesso')
        
        this.$sweet && this.$sweet.sweetSuccess && this.$sweet.sweetSuccess('Solicitação cancelada com sucesso!')
        
        // Recarregar dados
        await this.carregarDados()
      } catch (error) {
        console.error('❌ Erro ao cancelar solicitação:', error)
        
        let errorMessage = 'Erro ao cancelar solicitação.'
        if (error.response && error.response.data) {
          errorMessage = error.response.data.error || error.response.data.message || errorMessage
        }
        
        this.$sweet && this.$sweet.sweetError && this.$sweet.sweetError(errorMessage)
      }
    },

    getReembolsoDaInscricao(inscricao_id) {
      return this.reembolsosPorInscricao[inscricao_id] || null
    },

    getClasseStatus(status) {
      const classes = {
        'Solicitado': 'badge badge-warning',
        'Processando': 'badge badge-info',
        'Efetuado': 'badge badge-success',
        'Negado': 'badge badge-danger'
      }
      return classes[status] || 'badge badge-secondary'
    },

    formatarData(data) {
      if (!data) return '-';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof data === 'string') {
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '');
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T');
              const [year, month, day] = datePart.split('-');
              return `${day}/${month}/${year}`;
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return '-';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return '-';
      }
    },

    formatarValor(valor) {
      if (valor === null || valor === undefined || valor === '') return '0,00'
      return parseFloat(valor).toFixed(2).replace('.', ',')
    },

    formatarCPF(cpf) {
      if (!cpf) return ''
      const nums = String(cpf).replace(/\D/g, '')
      return nums.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
  }
}
</script>

<style lang="sass" scoped>
.area-restituicao
  background-color: #fff
  padding: 25px 15px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)

  h5
    font-size: 20px
    font-weight: 600
    text-align: center
    color: #2c3e50
    margin-bottom: 25px

    i
      margin-right: 10px
      color: #007bff

.section-header
  font-size: 16px
  font-weight: 600
  color: #2c3e50
  padding: 10px 15px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-left: 4px solid #007bff
  border-radius: 4px
  margin-bottom: 20px

  i
    margin-right: 8px
    color: #007bff

  span
    vertical-align: middle

.loading-container
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
  padding: 80px 20px
  min-height: 200px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 8px
  animation: fadeIn 0.3s ease

  .spinner-border
    width: 3rem
    height: 3rem
    border-width: 0.3rem

  p
    font-size: 15px
    color: #6c757d
    margin-top: 15px

.empty-state
  text-align: center
  padding: 60px 30px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 12px
  border: 2px dashed #dee2e6
  margin: 20px 0
  animation: fadeIn 0.5s ease

  i
    font-size: 64px
    color: #cbd5e0
    margin-bottom: 20px
    display: block
    animation: pulse 2s ease-in-out infinite

  .empty-title
    font-size: 18px
    font-weight: 600
    color: #495057
    margin: 15px 0 10px 0

  .empty-subtitle
    font-size: 14px
    color: #6c757d
    margin: 0 0 20px 0

  .empty-hint
    font-size: 12px
    color: #adb5bd
    font-style: italic
    margin-top: 10px

.table-responsive
  margin-top: 15px
  border-radius: 8px
  overflow: hidden
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)

.table
  margin-bottom: 0

  thead.table-dark
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)

    th
      color: #fff
      font-weight: 600
      border: none
      padding: 12px
      font-size: 14px

      i
        margin-right: 5px
        opacity: 0.8

  tbody
    tr
      transition: all 0.3s ease

      &:hover
        background-color: #f8f9fa
        transform: scale(1.002)

    td
      padding: 12px
      vertical-align: middle
      font-size: 14px

.badge
  font-size: 12px
  padding: 5px 12px
  border-radius: 12px
  font-weight: 600
  display: inline-block

  &.badge-warning
    background: #ffc107
    color: #000 !important

  &.badge-success
    background: #28a745
    color: #fff !important

  &.badge-danger
    background: #dc3545
    color: #fff !important

  &.badge-info
    background: #17a2b8
    color: #fff !important

  &.badge-secondary
    background: #6c757d
    color: #fff !important

.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0, 0, 0, 0.5)
  z-index: 1050
  display: flex
  align-items: center
  justify-content: center
  padding: 20px
  animation: fadeIn 0.3s ease

.modal-content
  background: white
  border-radius: 10px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3)
  max-width: 700px
  width: 90vw
  max-height: 90vh
  overflow: auto
  position: relative
  animation: slideInUp 0.3s ease

  &.large
    max-width: 900px

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 20px 25px
  border-bottom: 1px solid #e9ecef
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 10px 10px 0 0

  .modal-title
    margin: 0
    font-size: 18px
    font-weight: 600
    color: #2c3e50

    i
      margin-right: 8px
      color: #007bff

.modal-close
  background: none
  border: none
  font-size: 20px
  color: #6c757d
  cursor: pointer
  padding: 5px
  border-radius: 50%
  width: 35px
  height: 35px
  display: flex
  align-items: center
  justify-content: center
  transition: all 0.3s ease

  &:hover
    background: #e9ecef
    color: #dc3545

.modal-loading-overlay
  position: absolute
  top: 0
  left: 0
  right: 0
  bottom: 0
  background: rgba(255, 255, 255, 0.95)
  z-index: 10
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  border-radius: 10px

  .spinner-border
    width: 3rem
    height: 3rem
    border-width: 0.3rem

  p
    margin-top: 15px
    color: #6c757d
    font-size: 15px

.modal-body
  padding: 25px
  max-height: calc(90vh - 180px)
  overflow-y: auto

.modal-footer
  display: flex
  justify-content: flex-end
  gap: 10px
  padding: 20px 25px
  border-top: 1px solid #e9ecef
  background: #f8f9fa
  border-radius: 0 0 10px 10px

.info-box
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)
  padding: 20px
  border-radius: 8px
  border-left: 5px solid #2196f3

  .info-title
    font-size: 15px
    font-weight: 600
    color: #2c3e50
    margin-bottom: 15px
    display: flex
    align-items: center
    gap: 8px

    i
      color: #2196f3

  .info-row
    display: flex
    justify-content: space-between
    align-items: center
    padding: 8px 0
    border-bottom: 1px solid rgba(255,255,255,0.5)

    &:last-child
      border-bottom: none

    strong
      color: #2c3e50
      font-size: 14px

      i
        margin-right: 6px
        color: #2196f3

    span
      color: #495057
      font-size: 14px

.requerimento-box
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%)
  padding: 20px
  border-radius: 8px
  border-left: 4px solid #ffc107

  .text-requerimento
    font-size: 14px
    line-height: 1.8
    color: #2c3e50
    margin-bottom: 0
    text-align: justify

    strong
      color: #856404
      font-weight: 600

.form-group
  margin-bottom: 20px

.form-label
  font-weight: 600
  color: #2c3e50
  margin-bottom: 8px
  display: block
  font-size: 14px

  i
    margin-right: 6px
    color: #007bff

  .text-danger
    margin-left: 2px

.form-control
  border: 1px solid #ced4da
  border-radius: 6px
  padding: 10px 12px
  font-size: 14px
  transition: all 0.3s ease

  &:focus
    border-color: #007bff
    box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25)

.form-text
  font-size: 12px
  margin-top: 5px

.radio-group
  display: flex
  flex-direction: column
  gap: 10px

.radio-option
  display: flex
  align-items: center
  padding: 12px 15px
  background: #f8f9fa
  border: 2px solid #dee2e6
  border-radius: 6px
  cursor: pointer
  transition: all 0.3s ease

  &:hover
    background: #e9ecef
    border-color: #007bff

  input[type="radio"]
    margin-right: 10px
    cursor: pointer

    &:checked + span
      font-weight: 600
      color: #007bff

.radio-group-inline
  display: flex
  gap: 15px

.radio-option-inline
  display: flex
  align-items: center
  cursor: pointer

  input[type="radio"]
    margin-right: 6px
    cursor: pointer

.dados-bancarios-container, .chave-pix-container
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  padding: 20px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.05)

  h6
    font-size: 15px
    font-weight: 600
    color: #2c3e50
    margin-bottom: 15px

    i
      margin-right: 8px
      color: #007bff

.alert
  padding: 20px
  border-radius: 8px
  display: flex
  align-items: flex-start
  gap: 15px
  animation: slideInDown 0.4s ease

  i
    font-size: 24px
    margin-top: 2px
    flex-shrink: 0

  &.alert-info
    background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)
    border: 1px solid #bee5eb
    border-left: 5px solid #17a2b8
    color: #0c5460

    i
      color: #17a2b8

  div
    flex: 1

    strong
      display: block
      font-size: 15px
      margin-bottom: 8px
      font-weight: 600

    p
      font-size: 14px
      line-height: 1.6
      margin: 0

.btn
  border-radius: 6px
  padding: 10px 20px
  font-weight: 600
  transition: all 0.3s ease
  border: none
  font-size: 14px

  &:hover:not(:disabled)
    transform: translateY(-2px)
    box-shadow: 0 4px 12px rgba(0,0,0,0.2)

  &:active:not(:disabled)
    transform: translateY(0)

  &:disabled
    opacity: 0.6
    cursor: not-allowed
    transform: none

  i
    margin-right: 8px

  &.btn-sm
    padding: 6px 12px
    font-size: 13px

    i
      margin-right: 5px

  &.btn-xs
    padding: 4px 8px
    font-size: 11px

    i
      margin-right: 3px

  &.btn-outline-danger
    background: transparent
    border: 1px solid #dc3545
    color: #dc3545

    &:hover:not(:disabled)
      background: #dc3545
      color: #fff

  &.btn-primary
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%)

    &:hover:not(:disabled)
      background: linear-gradient(135deg, #0056b3 0%, #004085 100%)

  &.btn-success
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%)

    &:hover:not(:disabled)
      background: linear-gradient(135deg, #1e7e34 0%, #155724 100%)

  &.btn-info
    background: linear-gradient(135deg, #17a2b8 0%, #117a8b 100%)

    &:hover:not(:disabled)
      background: linear-gradient(135deg, #117a8b 0%, #0c5460 100%)

  &.btn-danger
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%)

    &:hover:not(:disabled)
      background: linear-gradient(135deg, #c82333 0%, #bd2130 100%)

  &.btn-secondary
    background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%)

    &:hover:not(:disabled)
      background: linear-gradient(135deg, #5a6268 0%, #545b62 100%)

// Utility classes
.d-flex
  display: flex

.flex-column
  flex-direction: column

.align-items-center
  align-items: center

.gap-1
  gap: 8px

.mt-1
  margin-top: 6px

@keyframes fadeIn
  from
    opacity: 0
  to
    opacity: 1

@keyframes slideInUp
  from
    opacity: 0
    transform: translateY(20px)
  to
    opacity: 1
    transform: translateY(0)

@keyframes slideInDown
  from
    opacity: 0
    transform: translateY(-20px)
  to
    opacity: 1
    transform: translateY(0)

@keyframes pulse
  0%, 100%
    transform: scale(1)
  50%
    transform: scale(1.05)

@media (max-width: 768px)
  .modal-content
    width: 95vw

  .table
    font-size: 12px

    th, td
      padding: 8px

  .area-restituicao
    padding: 15px 10px

    h5
      font-size: 18px

@media (max-width: 576px)
  .modal-content
    width: 98vw

  .section-header
    font-size: 14px
    padding: 8px 12px

  .empty-state
    padding: 40px 15px

    i
      font-size: 48px
</style>
